import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import ContactModal from './ContactModal';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c07ebe4f-8fac-4375-bd77-db80e53251b9/artifacts/3hq7a21m_flinch-logo-primary-dark.png";

const Header = () => {
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isContactOpen, setIsContactOpen] = useState(false);
  
  // Force dark mode styling on The Shift page
  const isShiftPage = location.pathname === '/shift';
  const effectiveDarkMode = isShiftPage ? true : isDarkMode;
  
  const bgClass = effectiveDarkMode 
    ? 'bg-[#0A0A0A]/90 backdrop-blur-md' 
    : 'bg-[#FAFAFA]/90 backdrop-blur-sm';
  
  const navLinkClass = effectiveDarkMode
    ? 'text-sm text-[#999999] hover:text-[#F2F2F2] transition-colors duration-300'
    : 'text-sm text-[#555555] hover:text-[#1A1A1A] transition-colors duration-300';
  
  const activeLinkClass = effectiveDarkMode
    ? 'text-sm text-[#F2F2F2] font-medium'
    : 'text-sm text-[#1A1A1A] font-medium';

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 ${bgClass} transition-all duration-300`}>
        <div className="flex justify-between items-center p-4 md:p-6 max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/" className="transition-opacity duration-300 hover:opacity-80">
            <img 
              src={LOGO_URL} 
              alt="FLINCH Logo - Strategic Marketing Agency" 
              className={`h-6 md:h-8 w-auto ${effectiveDarkMode ? 'brightness-0 invert' : ''}`}
            />
          </Link>
          
          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={isActive('/') ? activeLinkClass : navLinkClass}
            >
              Home
            </Link>
            <Link 
              to="/why-us" 
              className={isActive('/why-us') ? activeLinkClass : navLinkClass}
            >
              Why Us?
            </Link>
            <Link 
              to="/shift" 
              className={isActive('/shift') ? activeLinkClass : navLinkClass}
            >
              The Shift
            </Link>
            <Link 
              to="/insights" 
              className={isActive('/insights') ? activeLinkClass : navLinkClass}
            >
              Insights
            </Link>
          </nav>

          {/* Right Side: Theme Toggle (hidden on Shift page) + CTA */}
          <div className="flex items-center gap-4">
            {/* Dark Mode Toggle - Hidden on The Shift page */}
            {!isShiftPage && (
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-full transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-white/10 hover:bg-white/20 text-[#F2F2F2]' 
                    : 'bg-black/5 hover:bg-black/10 text-[#1A1A1A]'
                }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"/>
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                  </svg>
                )}
              </button>
            )}

            {/* CTA Button - Yellow for contrast */}
            <button 
              onClick={() => setIsContactOpen(true)}
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-[#F6CD17] text-black font-medium text-sm rounded-full hover:bg-[#e0b915] hover:shadow-[0_0_20px_rgba(246,205,23,0.4)] transition-all duration-300"
            >
              Talk to us
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </button>
          </div>
        </div>
      </header>
      
      {/* Contact Modal */}
      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default Header;
